import { Grid, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useQueryGetHubDetails } from '../../../../../../lockers/hubs.queries';
import { LockerDetails } from '../../../../../../lockers/models/locker-details.model';
import { BookOrderForm, CreateCustomerForm } from '../../book-order-action.hook';
import {
  KnownDeliveryContainerDtoAvailability,
  KnownDeliveryContainerDtoStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { FormControl } from '../../../../../../../ui/components/pure/form-control/form-control.component';
import { Dropdown, DropdownOption } from '../../../../../../../ui/components/pure/dropdown/dropdown.component';
import { naturalSort } from '../../../../../../../core/util/natural-sort.util';

interface LockerSelectorProps {
  hubID?: string;
  required?: boolean;
  limitByAvailability?: boolean;
}

export function LockerSelector({ hubID, required = false, limitByAvailability = false }: LockerSelectorProps) {
  const { control } = useFormContext<{
    bookOrderForm: BookOrderForm;
    createCustomerForm: CreateCustomerForm;
  }>();
  const { data: hubDetails, isLoading } = useQueryGetHubDetails(hubID === 'any' ? undefined : hubID);

  const lockerOptions = useMemo((): DropdownOption<string>[] => {
    if (!hubDetails) return [];

    const availableLockers: LockerDetails[] = [];
    hubDetails.towers?.forEach((tower) => {
      tower.lockers.forEach((locker) => {
        // First filster by status
        if (!locker.isKiosk && locker.status === KnownDeliveryContainerDtoStatus.Active) {
          // Then filter by availability if needed
          if (limitByAvailability) {
            if (locker.availability === KnownDeliveryContainerDtoAvailability.Available) {
              availableLockers.push(locker);
            }
          } else {
            availableLockers.push(locker);
          }
        }
      });
    });

    const options = availableLockers.map((locker) => ({
      label: locker.name,
      value: locker.id
    }));

    return naturalSort(options).asc((option) => option.label);
  }, [hubDetails]);

  if (!hubID || hubID === 'any') {
    return (
      <Typography variant="body2" color="textSecondary">
        Please select a specific hub to view available lockers
      </Typography>
    );
  }

  if (isLoading) {
    return <Typography variant="body2">Loading available lockers...</Typography>;
  }

  if (!hubDetails) {
    return (
      <Typography variant="body2" color="error">
        Unable to load locker information for this hub
      </Typography>
    );
  }

  if (lockerOptions.length === 0 && !isLoading && hubDetails) {
    return (
      <Typography variant="body2" color="textSecondary">
        No active lockers at this hub
      </Typography>
    );
  }

  return (
    <Grid container sx={{ flexDirection: 'column' }} spacing={1}>
      <Grid>
        <FormControl
          control={control}
          name="bookOrderForm.lockerID"
          Component={Dropdown}
          rules={{
            required: required ? 'Please select a locker' : false
          }}
          ComponentProps={{
            fullWidth: true,
            required: required,
            dropdownVariant: 'floating' as const,
            placeholder: 'Select Locker',
            menuItems: lockerOptions
          }}
        />
      </Grid>
    </Grid>
  );
}
